import type {
  Brand,
  GetOutgoingInvoicesQuery,
  InvoicePaymentMethodFragment,
  OutgoingInvoice,
  OutgoingInvoiceStats,
  Project,
} from "@src/__generated__/graphql";
import { appStore } from "@src/stores/AppStore";
import type { TWorkspaceSettings } from "@src/stores/models/types";
import { map } from "lodash";

type TOutgoingInvoice = NonNullable<
  GetOutgoingInvoicesQuery["outgoingInvoices"]
>["data"][0];
type TBrand = NonNullable<TOutgoingInvoice["brands"]>[0];
type TProjects = NonNullable<TOutgoingInvoice["projects"]>[0];

export class OutgoingInvoiceModel {
  id: OutgoingInvoice["id"];
  type: OutgoingInvoice["type"];
  invoice_no: OutgoingInvoice["invoice_no"];
  brands: BrandModel[];
  projects: ProjectsModel[];

  has_proforma_payments: OutgoingInvoice["has_proforma_payments"];
  proformaInvoicesPivot: NonNullable<
    TOutgoingInvoice["proformaInvoicesPivot"]
  >[0]["invoice"][];
  abraflexi_exported: OutgoingInvoice["abraflexi_exported"];
  xero_exported: OutgoingInvoice["xero_exported"];
  documentExports: TOutgoingInvoice["documentExports"];
  stats: {
    total_amount: OutgoingInvoiceStats["total_amount"];
    total_amount_with_vat: OutgoingInvoiceStats["total_amount_with_vat"];
    total_amount_in_currency: OutgoingInvoiceStats["total_amount_in_currency"];
    total_amount_with_vat_in_currency: OutgoingInvoiceStats["total_amount_with_vat_in_currency"];
    billing_amount: OutgoingInvoiceStats["billing_amount"];
    billing_amount_with_vat: OutgoingInvoiceStats["billing_amount_with_vat"];
  };
  due_date: Date;
  currency: OutgoingInvoice["currency"];
  issue_date: Date;
  date_of_supply: Date;
  paid_at: Date | null;
  note: OutgoingInvoice["note"];
  internal_note: OutgoingInvoice["internal_note"];
  emailLogs: TOutgoingInvoice["emailLogs"];
  owner: TOutgoingInvoice["owner"];
  invoice_email: TOutgoingInvoice["billingEntity"]["invoice_email"];
  contact: TOutgoingInvoice["contact"];
  invoicePdf: TOutgoingInvoice["invoicePdf"];
  relatesToProformaPdf: NonNullable<
    TOutgoingInvoice["relatesToOutgoingProformaInvoice"]
  >["invoicePdf"];
  workspaceCompany: TWorkspaceSettings["companies"][0] | undefined | null;
  submitted_at: TOutgoingInvoice["request_for_invoicing_submit_date"];
  created_at: TOutgoingInvoice["request_for_invoicing_created_date"];
  accountMoves: TOutgoingInvoice["accountMoves"];
  exchange_rate: TOutgoingInvoice["exchange_rate"];
  paid: number | undefined;
  projectTimeBilling?: { id: string; is_locked: boolean };
  invoicePaymentMethod?: InvoicePaymentMethodFragment | null;
  modelLock: TOutgoingInvoice["modelLock"];

  constructor(src: TOutgoingInvoice) {
    this.id = src.id;
    this.type = src.type;
    this.invoice_no = src.invoice_no;
    this.note = src.note;
    this.internal_note = src.internal_note;
    this.has_proforma_payments = src.has_proforma_payments;
    this.proformaInvoicesPivot = src.proformaInvoicesPivot.map(
      ({ invoice }) => invoice,
    );
    this.abraflexi_exported = src.abraflexi_exported;
    this.xero_exported = src.xero_exported;
    this.documentExports = src.documentExports;
    this.emailLogs = src.emailLogs;
    this.due_date = new Date(src.due_date);
    this.currency = src.currency;
    this.issue_date = new Date(src.issue_date);
    this.date_of_supply = new Date(src.date_of_supply);
    this.paid_at = src.paid_at ? new Date(src.paid_at) : null;
    this.brands = map(src.brands, (b) => new BrandModel(b));
    this.stats = src.stats;
    this.projects = map(src.projects, (p) => new ProjectsModel(p));
    this.invoice_email = src.billingEntity.invoice_email;
    this.contact = src.contact;
    this.owner = src.owner;
    this.invoicePdf = src.invoicePdf;
    this.relatesToProformaPdf =
      src.relatesToOutgoingProformaInvoice?.invoicePdf;
    this.workspaceCompany = appStore.workspaceStore.getCompany(
      src.workspace_company_id,
    );
    this.submitted_at = src.request_for_invoicing_submit_date;
    this.created_at = src.request_for_invoicing_created_date;
    this.accountMoves = src.accountMoves;
    this.exchange_rate = src.exchange_rate;
    this.paid =
      Array.isArray(src.accountMoves) && src.accountMoves.length > 0
        ? src.accountMoves.reduce((prev, curr) => {
            return prev + curr.amount_in_move_currency;
          }, 0)
        : undefined;
    this.projectTimeBilling = src.projectTimeBilling ?? undefined;
    this.modelLock = src.modelLock;
    this.invoicePaymentMethod = src.invoicePaymentMethod ?? null;
  }
}

class BrandModel {
  id: Brand["id"];
  name: Brand["name"];
  constructor(src: TBrand) {
    this.name = src.name;
    this.id = src.id;
  }
}

class ProjectsModel {
  id: Project["id"];
  title: Project["title"];
  code: Project["code"];
  constructor(src: TProjects) {
    this.id = src.id;
    this.title = src.title;
    this.code = src.code;
  }
}
